import { getSceneDurationInSeconds } from "./getSceneDurationInSeconds.js";
import { getSceneComplexity } from "./getSceneComplexity.js";
import { getCompositionDurationInSeconds } from "./getCompositionDurationInSeconds.js";
import { toFrames } from "./frames-lib.js";
export function getComplexityAtFrame(frame, renderConfig, theme) {
    let frameCounter = 0;
    const scenes = theme.scenes;
    const fps = renderConfig.videoFrame.fps;
    for (let i = 0; i < scenes.length; i++) {
        const scene = scenes[i];
        // just for TS
        if (!scene) {
            continue;
        }
        const sceneDuration = getSceneDurationInSeconds(scene, renderConfig, theme);
        const sceneEndFrame = frameCounter + toFrames(sceneDuration, fps);
        if (frame < sceneEndFrame) {
            return getSceneComplexity(scene, renderConfig);
        }
        frameCounter = sceneEndFrame;
    }
    // This is an unlikely case, so we'll just use the worst-case scenario
    // Namely the scene with the highest complexity
    return scenes.reduce((prev, scene) => {
        const complexity = getSceneComplexity(scene, renderConfig);
        return Math.max(prev, complexity);
    }, 0);
}
export function calculateChunkFrameRange({ renderConfig, theme, chunkNumber, chunkCount, }) {
    if (chunkCount <= 0) {
        throw new Error(`Expected a positive number of chunks; Got ${chunkCount}`);
    }
    if (chunkNumber < 1 || chunkNumber > chunkCount) {
        throw new Error(`Expected chunkNumber to be between 1 and ${chunkCount}; Got ${chunkNumber}`);
    }
    const { videoFrame: { fps }, } = renderConfig;
    const durationInSeconds = getCompositionDurationInSeconds(renderConfig, theme);
    if (durationInSeconds <= 0) {
        throw new Error(`Expected a positive duration in seconds; Got ${durationInSeconds}`);
    }
    const durationInFrames = toFrames(durationInSeconds, fps);
    // Highly unlikely situation, but we'll handle it anyway
    if (durationInFrames < chunkCount) {
        chunkCount = Math.min(durationInFrames, chunkCount);
    }
    const frameComplexity = Array.from({ length: durationInFrames }).map((_, i) => Math.ceil(getComplexityAtFrame(i, renderConfig, theme)));
    // Expand the complexity array so each point of complexity gets its own array
    // item pointing to the original frame
    const expandedFrameComplexity = frameComplexity.flatMap((complexity, i) => {
        return Array.from({ length: complexity }).map(() => i);
    });
    // split durationInFrames into `chunkCount` chunks, and figure out how big
    // each chunk should be
    const weightedChunkSize = Math.ceil(expandedFrameComplexity.length / chunkCount);
    const frameRanges = [];
    for (let i = 0; i < chunkCount; i++) {
        const expandedFrameComplexityStartIndex = i * weightedChunkSize;
        let start = expandedFrameComplexity[i * weightedChunkSize];
        if (start === undefined) {
            throw new Error(`expandedFrameComplexity not defined at start index ${expandedFrameComplexityStartIndex}`);
        }
        const expandedFrameComplexityEndIndex = Math.min((i + 1) * weightedChunkSize - 1, expandedFrameComplexity.length - 1);
        const end = expandedFrameComplexity[Math.min((i + 1) * weightedChunkSize - 1, expandedFrameComplexity.length - 1)];
        if (end === undefined) {
            throw new Error(`expandedFrameComplexity not defined at end index ${expandedFrameComplexityEndIndex}`);
        }
        if (i > 0) {
            const previousRange = frameRanges[i - 1];
            if (previousRange && previousRange[1] === start) {
                start += 1;
            }
        }
        const range = [start, end];
        if (i + 1 === chunkNumber) {
            return range;
        }
        else {
            frameRanges.push(range);
        }
    }
    return [0, durationInFrames - 1];
}
export function calculateChunks({ chunkCount, compilationId, }) {
    if (chunkCount <= 0) {
        throw new Error(`Expected a positive number of chunks; Got ${chunkCount}`);
    }
    const chunks = [];
    for (let i = 0; i < chunkCount; i++) {
        const chunkNumber = i + 1;
        const chunkId = `chunk-${compilationId}-${chunkNumber}-${chunkCount}`;
        chunks.push({
            chunkId,
            chunkNumber,
            chunkCount,
            failures: 0,
            chunkS3Key: `${compilationId}/${chunkId}.mp4`,
            status: "pending",
        });
    }
    return chunks;
}
export function lookupChunk(chunks, chunkId) {
    return chunks.find((chunk) => {
        return chunk.chunkId === chunkId;
    });
}
